import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { UserServiceModel } from './user.service';
import { Topic, TopicField } from '../utils/topic-utils';
import { FolderManagementServiceModel } from '@app/origami-api/services/folder-management.service';
import Workflow = FolderManagementServiceModel.Workflow;
import AvailableWorkflowConfigurations = WorkflowManagementServiceModel.AvailableWorkflowConfigurations;

export namespace WorkflowManagmentServiceOptions {
  import WorkflowStageContributorStatus = FolderManagementServiceModel.WorkflowStageContributorStatus;

  export interface ReleaseWorkflowTopic {
    siteId: number;
    topicId: number;
    workflowStageId: number;
  }

  export interface ReleaseWorkflowTopicMultiple {
    siteId: number;
    topicIds: number[];
    workflowId: number;
  }

  export interface ClaimWorkflowTopicMultiple {
    siteId: number;
    topicIds: number[];
    workflowId: number;
    workflowStageId?: number;
    claimUserName?: string;
  }

  export interface GetWorkflowById {
    includeStages: boolean;
    siteId: number;
    workflowId: number;
  }

  export interface StartWorkflow {
    siteId: number;
    documentTopicIds: number[];
    workflowTopicIds: number[];
  }

  export interface StartTopicWorkflowReview {
    siteId: number;
    topicId: number;
  }

  export interface ProcessTopicWorkflowReview {
    siteId: number;
    topicId: number;
    stageReviewStatusId: number;
    stageReviewIssues?: string;
    stageReviewComments?: string;
  }

  export interface TopicWorkflowStageConfigurationApprover {
    GroupIds: string[];
    UserNames: string[];
    GroupNames?: string[];
  }

  export enum QMSWorkflowAccessMode {
    EntireTeam = 0,
    AdHocTeam = 1,
  }

  export interface TopicWorkflowStageConfiguration {
    WorkflowStageId: number;
    WorkflowStageName?: string;
    MinimumNumberOfApprovers?: number;
    Approvers?: TopicWorkflowStageConfigurationApprover;
    Contributors?: TopicWorkflowStageConfigurationApprover;
    Comments?: string;
    ContributorComments?: string;
  }

  export interface TopicWorkflowConfiguration {
    WorkflowId: number;
    WorkflowName?: string;
    TrainingNeeded?: boolean;
    TopicWorkflowStageConfigurations: TopicWorkflowStageConfiguration[];
    AccessMode?: QMSWorkflowAccessMode;
  }

  export interface StartCustomWorkflow {
    documentTopicIds: number[];
    siteId: number;
    workflowTopicIds: number[];
    topicWorkflowConfigurations: TopicWorkflowConfiguration[];
    createReviewDocument?: boolean;
  }

  export enum ParallelReviewType {
    claim = 1,
    release = 2,
  }

  export interface WorkflowStateData {
    Status: number;
    WorkflowId: number;
  }

  export interface StartQualityManagementWorkflow {
    siteId: number;
    recordTopicId: number;
    workflowTopicId?: number;
    comments?: string;
    topicFields?: TopicField[];
    topicWorkflowConfiguration?: TopicWorkflowConfiguration;
  }

  export interface EffectivenessCheckParams {
    ValidateEffectivenessCheck?: boolean;
    EffectivenessCheckRefused?: boolean;
    RefuseEffectivenessCheckReason?: string;
    CreatedECTopicId?: number;
  }

  export interface ProcessQualityManagementWorkflow {
    siteId: number;
    topicId: number;
    stageId: number;
    stageReviewStatusId: number;
    stageReviewComments?: string;
    processParams?: EffectivenessCheckParams;
    topicWorkflowConfiguration?: TopicWorkflowConfiguration;
  }

  export interface UpdateTopicWorkflowConfiguration {
    siteId: number;
    topicId: number;
    topicFields?: TopicField[];
    topicWorkflowConfiguration?: TopicWorkflowConfiguration;
    comments?: string;
  }

  export interface AddWorkflowStageContributorLog {
    siteId: number;
    workflowStageId: number;
    topicId: number;
    comments: string;
    statusId: WorkflowStageContributorStatus;
  }

  export interface AddRemoveWorkflowStageContributors {
    siteId: number;
    topicId: number;
    userNames: string;
    comments?: string;
    workflowStageId: number;
    statusId?: WorkflowStageContributorStatus;
  }
  export enum TriggerType {
    NewDocument = 1,
    FieldChanged = 2,
    Manual = 4,
  }

  export interface GetApplicableWorkflowsForTopics {
    siteId: number;
    topicIds: number[];
    workflowTrigger: TriggerType;
    workflowTopicIds?: number[];
    startCustomWorkflow: boolean;
  }
}

export namespace WorkflowManagementServiceModel {
  import QMSWorkflowAccessMode = WorkflowManagmentServiceOptions.QMSWorkflowAccessMode;

  export interface ChangeControlWorkflowDocumentStatus {
    DisplayDocumentTopicId: number;
    DocumentTopicId: number;
    ReviewDocumentTopicId: number;
    Status: number;
  }

  export interface TopicWorkflowConfigurations {
    AccessMode: QMSWorkflowAccessMode;
    ConfigurationId: number;
    TopicId: number;
    TopicWorkflowStageConfigurations: TopicWorkflowStageConfiguration[];
    TrainingNeeded: boolean;
    WorkflowId: number;
  }

  export interface AvailableWorkflowConfigurations {
    TopicId: number;
    Workflows: AvailableWorkflowConfigurationItem[];
  }

  export interface AvailableWorkflowConfigurationItem {
    WorkflowId: number;
    WorkflowName: string;
  }

  export interface TopicWorkflowStageConfiguration {
    ApproverGroupId: number | null;
    ApproverUsers: StageUser[];
    ContributorUsers: StageUser[];
    ApproverGroups: StageGroup[];
    ConfigurationId: number;
    ContributorGroupId: number;
    MinimumNumberOfApprovers: number;
    TopicWorkflowStageConfigurationId: number;
    WorkflowStageId: number;
    WorkflowStageName: string;
    WorkflowStageApproverGroupId: number;
    WorkflowStageAuthorityTypeId: number;
    Comments: string;
    AssignOwner?: boolean;
    ContributorComments: string;
  }

  export interface StageUser {
    DomainId: number;
    EmailAddress: string;
    FirstName: string;
    FullName: string;
    IsUserSitesMember: boolean;
    LastName: string;
    MobileNo: string;
    PhoneNo: string;
    RoleLevel: number;
    SiteId: number;
    UserId: number;
    UserName: string;
  }
  export interface StageGroup {
    CreatedBy: number;
    CreatedDate: string;
    Description: string;
    DisplayName: string;
    DomainId: number;
    GroupId: number;
    GroupName: string;
    GroupTypeId: number;
    IsSystemGroup: false;
    LastUpdated: string;
    LastUpdatedBy: number;
    StatusId: number;
  }

  export interface WorkflowUser {
    Actions: UserServiceModel.Action[];
    Address: string;
    AllowMultipleSessions: boolean;
    City: string;
    Country: string;
    CreatedBy: number;
    CreatedDate: string;
    DomainId: number;
    EmailAddress: string;
    FirstName: string;
    FullName: string;
    GlobalUserTopic: Topic;
    Groups: any;
    IsEmailAutogenerated: boolean;
    IsLocked: boolean;
    IsSsoEnabled: boolean;
    IsUserSitesMember: boolean;
    LastName: string;
    LastUpdated: string;
    LastUpdatedBy: number;
    LoginName: string;
    MFARegistered: any;
    MachineId: any;
    MobileNo: string;
    OrganizationName: string;
    PasswordDate: string;
    PasswordExpirationDate: string;
    PhoneNo: string;
    RoleLevel: number;
    State: string;
    StatusId: number;
    TemporaryPasswordExpirationDate: string;
    Title: string;
    UserId: number;
    UserName: string;
    UserTopic: Topic;
    UserTypeId: number;
    ZipCode: string;
  }
}

@Injectable()
export class WorkflowManagementService extends ServiceBase {
  serviceUrl = 'WorkflowManagmentService.svc';

  createWorkflow(params: { siteId: number; definition: Workflow }, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/CreateWorkflow', params, null, silentErrorCodes, true);
  }

  updateWorkflow(params: { siteId: number; definition: Workflow }, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/UpdateWorkflow', params, null, silentErrorCodes, true);
  }

  replaceWorkflow(
    params: { siteId: number; workflowIdToReplace: number; includeExcludedDocs: boolean; definition: Workflow },
    silentErrorCodes?: ErrorCode[],
  ) {
    return this.origamiPost('/ReplaceWorkflow', params, null, silentErrorCodes, true);
  }

  setWorkflowState(
    params: { siteId: number; states: WorkflowManagmentServiceOptions.WorkflowStateData[] },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<OrigamiHttpResponse>> {
    return this.origamiPost('/SetWorkflowState', params, null, silentErrorCodes, true);
  }

  deleteWorkflows(
    params: { siteId: number; workflowIds: number[] },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<OrigamiHttpResponse>> {
    return this.origamiPost('/DeleteWorkflows', params, null, silentErrorCodes, true);
  }

  getChangeControlWorkflowDocumentStatus(
    params: { siteId: number; topicId: number },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<WorkflowManagementServiceModel.ChangeControlWorkflowDocumentStatus>> {
    return this.origamiPost('/GetChangeControlWorkflowDocumentStatus', params, null, silentErrorCodes, true);
  }

  releaseWorkflowTopic(
    params: { siteId: number; topicId: number; workflowStageId: number },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/ReleaseWorkflowTopic', params, null, silentErrorCodes, true);
  }

  releaseWorkflowTopicMultiple(
    body: WorkflowManagmentServiceOptions.ReleaseWorkflowTopicMultiple,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ReleaseWorkflowTopicMultiple', body, null, null, true);
  }

  claimWorkflowTopic(
    params: { siteId: number; topicId: number; workflowStageId: number; claimUserName?: string; comments?: string },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/ClaimWorkflowTopic', params, null, silentErrorCodes, true);
  }

  claimWorkflowTopicMultiple(
    body: WorkflowManagmentServiceOptions.ClaimWorkflowTopicMultiple,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ClaimWorkflowTopicMultiple', body, null, silentErrorCodes, true);
  }

  getWorkflowById(
    body: WorkflowManagmentServiceOptions.GetWorkflowById,
    ...params
  ): Observable<OrigamiResponse<FolderManagementServiceModel.Workflow>> {
    return this.origamiPost('/GetWorkflowById', body, ...params);
  }

  startCustomWorkflow(body: WorkflowManagmentServiceOptions.StartCustomWorkflow): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/StartCustomWorkflow', body, null, null, true);
  }

  startWorkflow(body: WorkflowManagmentServiceOptions.StartWorkflow): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/StartWorkflow', body, null, null, true);
  }

  startTopicWorkflowReview(
    body: WorkflowManagmentServiceOptions.StartTopicWorkflowReview,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/StartTopicWorkflowReview', body, null, null, true);
  }

  processTopicWorkflowReview(
    body: WorkflowManagmentServiceOptions.ProcessTopicWorkflowReview,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ProcessTopicWorkflowReview', body, null, null, true);
  }

  parallelReviewWorkflowTopic(body: {
    siteId: number;
    topicId: number;
    workflowStageId: number;
    parallelReviewType: WorkflowManagmentServiceOptions.ParallelReviewType;
  }): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ParallelReviewWorkflowTopic', body, null, null, true);
  }

  excludeTopicsFromWorkflow(
    body: {
      siteId: number;
      topicsToExclude: { TopicIds: number[]; WorkflowId: number; ExcludeAll: boolean }[];
      exclusionReason: string;
    },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ExcludeTopicsFromWorkflow', body, null, silentErrorCodes, true);
  }

  submitWorkflowTopicToStage(body: {
    siteId: number;
    topicId: number;
    workflowStageId: number;
  }): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SubmitWorkflowTopicToStage', body, null, null, true);
  }

  submitWorkflowTopicToStageMultiple(body: {
    siteId: number;
    topicIds: number[];
    workflowStageId: number;
  }): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SubmitWorkflowTopicToStageMultiple', body, null, null, true);
  }

  getTopicWorkflowEsignatureUsers(
    body: { siteId: number; topicId: number; workflowId: number },
    ...params
  ): Observable<OrigamiResponse<{ UserName: string; FullNAme: string }[]>> {
    return this.origamiPost('/GetTopicWorkflowEsignatureUsers', body, ...params);
  }

  getTopicWorkflowConfigurations(body: {
    siteId: number;
    topicIds: number[];
    workflowId: number;
  }): Observable<OrigamiResponse<WorkflowManagementServiceModel.TopicWorkflowConfigurations[]>> {
    return this.origamiPost('/GetTopicWorkflowConfigurations', body, null, null, true);
  }

  startQualityManagementWorkflow(body: WorkflowManagmentServiceOptions.StartQualityManagementWorkflow) {
    return this.origamiPost(
      '/StartQualityManagementWorkflow',
      body,
      null,
      [ErrorCode.FINISHED_WITH_MINOR_ERRORS, ErrorCode.SELECT_WORKFLOW, ErrorCode.NO_MATCHING_WF],
      true,
    );
  }

  processQualityManagementWorkflow(body: WorkflowManagmentServiceOptions.ProcessQualityManagementWorkflow) {
    return this.origamiPost(
      '/ProcessQualityManagementWorkflow',
      body,
      null,
      [ErrorCode.FINISHED_WITH_MINOR_ERRORS, ErrorCode.EFFECTIVENESS_CHECK_IS_MISSING],
      true,
    );
  }

  updateTopicWorkflowConfiguration(body: WorkflowManagmentServiceOptions.UpdateTopicWorkflowConfiguration) {
    return this.origamiPost('/UpdateTopicWorkflowConfiguration', body, null, null, true);
  }

  addWorkflowStageContributorLog(body: WorkflowManagmentServiceOptions.AddWorkflowStageContributorLog) {
    return this.origamiPost('/AddWorkflowStageContributorLog', body, null, null, true);
  }

  addTopicWorkflowStageContributors(body: WorkflowManagmentServiceOptions.AddRemoveWorkflowStageContributors) {
    return this.origamiPost('/AddTopicWorkflowStageContributors', body, null, null, true);
  }

  removeTopicWorkflowStageContributors(body: WorkflowManagmentServiceOptions.AddRemoveWorkflowStageContributors) {
    return this.origamiPost('/RemoveTopicWorkflowStageContributors', body, null, null, true);
  }
  getApplicableWorkflowsForTopics(
    body: WorkflowManagmentServiceOptions.GetApplicableWorkflowsForTopics,
  ): Observable<OrigamiResponse<AvailableWorkflowConfigurations[]>> {
    return this.origamiPost('/GetApplicableWorkflowsForTopics', body, null, null, true);
  }
  getAdHocWorkflowConfiguration(body: {
    siteId: number;
    teamId: number;
    workflowId: number;
  }): Observable<OrigamiResponse<WorkflowManagementServiceModel.TopicWorkflowConfigurations>> {
    return this.origamiPost('/GetAdHocWorkflowConfiguration', body, null, null, true);
  }
}
